import { isObjectRecord } from "../../ars/typeGuards/isObjectRecord";
import { isMosaicTrafficLightsValue, MosaicTrafficLightsValue } from "../../traffic-lights";
import { GridItemChangeType } from "./interface";

/**
 * Traffic light information associated with a grid item (row/column/group).
 */

export interface GridItemTrafficLight {
  /** Core traffic light value. */
  coreTrafficLight: MosaicTrafficLightsValue;
  /** The identifier for the grid item. */
  gridItemId: string;
  /** Optional id used after a traffic light is shown. */
  idAfterTrafficLightIsShown?: string;
  /** When true, the grid item is optional. */
  isOptional: boolean;
  /** The change type associated with the grid item. */
  gridItemChangeType: GridItemChangeType;
}

/**
 * Type guard that asserts whether a value conforms to the GridItemTrafficLight shape.
 *
 * @param obj - Unknown value to test. Typically an object parsed from JSON or coming from
 *        an external source where the exact type is not known.
 * @returns True when `obj` matches the `GridItemTrafficLight` interface; otherwise false.
 *          This function is a type predicate (`obj is GridItemTrafficLight`) so callers can
 *          narrow the value's type after a successful check.
 */
export const isGridItemTrafficLight = (obj: unknown): obj is GridItemTrafficLight => {
  // Check object
  if (!isObjectRecord(obj)) return false;

  // Check basic property types
  if (
    typeof obj.coreTrafficLight !== "string" ||
    typeof obj.gridItemId !== "string" ||
    typeof obj.isOptional !== "boolean" ||
    typeof obj.gridItemChangeType !== "string"
  )
    return false;

  // Check optional property types
  if (
    obj.idAfterTrafficLightIsShown !== undefined &&
    typeof obj.idAfterTrafficLightIsShown !== "string"
  )
    return false;

  // Check enums
  if (!isMosaicTrafficLightsValue(obj.coreTrafficLight)) return false;

  // All good
  return true;
};
